import React, { useState, useRef, useEffect } from 'react';
import { MdSend, MdAutoAwesome, MdPerson, MdClose, MdCheckCircle, MdDelete } from 'react-icons/md';
import { chatWithTeacherAssistant } from '../../services/aiService';

const TeacherAIAssistant = () => {
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('teacherAiChatHistory');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) { }
    }
    return [
      { role: 'model', text: 'Hello! I am your AI Teaching Assistant. I can help you generate quiz questions, draft lesson plans, or answer questions about your subjects. How can I help you today?' }
    ];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    localStorage.setItem('teacherAiChatHistory', JSON.stringify(messages));
  }, [messages]);

  const handleClearChat = () => {
    if (window.confirm("Are you sure you want to delete your entire chat history?")) {
      const defaultMessage = [{ role: 'model', text: 'Hello! I am your AI Teaching Assistant. I can help you generate quiz questions, draft lesson plans, or answer questions about your subjects. How can I help you today?' }];
      setMessages(defaultMessage);
      localStorage.setItem('teacherAiChatHistory', JSON.stringify(defaultMessage));
    }
  };

  const formatMessage = (text) => {
    return text.split('\n').map((line, lineIndex) => {
      // Handle horizontal rule
      if (line.trim() === '---') {
        return <hr key={lineIndex} className="my-5 border-gray-200" />;
      }

      let isListItem = false;
      let content = line;
      let listIcon = null;

      // Handle unordered lists (* or -)
      if (line.trim().startsWith('* ') || line.trim().startsWith('- ')) {
        isListItem = true;
        content = line.trim().substring(2);
        listIcon = <MdCheckCircle className="text-green-500 inline-block mr-3 flex-shrink-0 mt-0.5" size={20} />;
      }
      // Handle ordered lists (1. 2. etc)
      else if (/^\d+\.\s/.test(line.trim())) {
        isListItem = true;
        const match = line.trim().match(/^(\d+\.)\s(.*)/);
        listIcon = <span className="text-indigo-600 font-black mr-2 inline-block flex-shrink-0 w-6 mt-0.5">{match[1]}</span>;
        content = match[2];
      }

      // Handle bold **text**
      const parts = content.split(/(\*\*.*?\*\*)/g);
      const formattedContent = parts.map((part, partIndex) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={partIndex} className="font-bold text-gray-900">{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      if (isListItem) {
        return (
          <div key={lineIndex} className="flex items-start mb-3 ml-2 bg-gray-50/50 p-2 rounded-lg hover:bg-gray-50 transition-colors">
            {listIcon}
            <span className="flex-1 text-gray-700">{formattedContent}</span>
          </div>
        );
      }

      if (line.trim() === '') {
        return <div key={lineIndex} className="h-2"></div>;
      }

      // Headings (basic support)
      if (line.trim().startsWith('#')) {
        return <h3 key={lineIndex} className="text-lg font-bold text-indigo-900 mt-4 mb-2">{formattedContent}</h3>;
      }

      return (
        <p key={lineIndex} className="mb-3 text-gray-700">
          {formattedContent}
        </p>
      );
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsLoading(true);

    try {
      // Create history array without the newest user message, as it's sent separately
      const history = messages.filter(m => m.role !== 'system');

      const response = await chatWithTeacherAssistant(userMsg, history);

      setMessages(prev => [...prev, { role: 'model', text: response.answer || response.text }]);
    } catch (error) {
      console.error(error);
      setMessages(prev => [...prev, { role: 'model', text: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full h-[calc(100vh-80px)] flex flex-col py-4 md:py-6">
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-md">
            <MdAutoAwesome size={24} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-darkNavy font-poppins">AI Assistant</h1>
            <p className="text-sm md:text-base text-gray-500">Your personal teaching assistant</p>
          </div>
        </div>

        <button
          onClick={handleClearChat}
          className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition-colors border border-red-100 shadow-sm"
          title="Delete Chat History"
        >
          <MdDelete size={18} />
          Clear Chat
        </button>
      </div>

      <div className="flex-1 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto px-2 py-4 sm:p-6 space-y-4 sm:space-y-6 bg-gray-50/50">
          {messages.map((msg, index) => (
            <div key={index} className={`w-full flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {msg.role === 'user' && (
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary text-white flex-shrink-0">
                  <MdPerson size={20} />
                </div>
              )}
              <div className={`rounded-2xl p-3.5 sm:p-5 shadow-sm ${msg.role === 'user' ? 'max-w-[85%] bg-primary text-white rounded-tr-none' : 'w-full bg-white border border-gray-100 text-gray-800 leading-relaxed'}`}>
                {msg.role === 'user' ? (
                  <p className="whitespace-pre-wrap">{msg.text}</p>
                ) : (
                  <div className="text-[15px]">
                    {formatMessage(msg.text)}
                  </div>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="w-full flex gap-4">
              <div className="w-full bg-white border border-gray-100 rounded-2xl p-3 sm:p-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-100">
          <form onSubmit={handleSend} className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
                placeholder="Ask me what you want..."
                className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3 px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 resize-none max-h-32 transition-all"
                rows="1"
                style={{ minHeight: '50px' }}
              />
            </div>
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              <MdSend size={20} className="ml-1" />
            </button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-3 font-medium">
            AI can make mistakes. Consider verifying important information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeacherAIAssistant;
